import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Card,
  DataTable,
  Badge,
  Button,
  Filters,
  ChoiceList,
  TextField,
  InlineStack,
  Text,
  Pagination,
  BlockStack,
  SkeletonBodyText,
  EmptyState,
  Box,
  Modal,
} from '@shopify/polaris';
import { doctorsApi } from '@/services';
import type { Doctor } from '@/types';

export default function DoctorsList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newDoctor, setNewDoctor] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const limit = 20;

  const { data, isLoading, error } = useQuery({
    queryKey: ['doctors', page, status, searchQuery],
    queryFn: () =>
      doctorsApi.list({
        page,
        limit,
        status: status[0],
        search: searchQuery || undefined,
      }),
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof newDoctor) => doctorsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doctors'] });
      setShowCreateModal(false);
      setNewDoctor({ firstName: '', lastName: '', email: '', phone: '' });
    },
  });

  const handleStatusChange = useCallback((value: string[]) => {
    setStatus(value);
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    setPage(1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setStatus([]);
    setSearchQuery('');
  }, []);

  const handleCreateDoctor = useCallback(() => {
    if (!newDoctor.firstName || !newDoctor.lastName) return;
    createMutation.mutate(newDoctor);
  }, [newDoctor, createMutation]);

  const getStatusBadge = (doctorStatus: string) => {
    switch (doctorStatus) {
      case 'active':
        return <Badge tone="success">Active</Badge>;
      case 'inactive':
        return <Badge>Inactive</Badge>;
      default:
        return <Badge>{doctorStatus}</Badge>;
    }
  };

  const rows = (data?.data || []).map((doctor: Doctor) => [
    <Button key={doctor.id} variant="plain" onClick={() => navigate(`/doctors/${doctor.id}`)}>
      {`${doctor.firstName} ${doctor.lastName}`}
    </Button>,
    doctor.email || 'N/A',
    doctor.phone || 'N/A',
    doctor._count?.patients ?? 0,
    getStatusBadge(doctor.status),
    <Button key={`view-${doctor.id}`} onClick={() => navigate(`/doctors/${doctor.id}`)}>
      View
    </Button>,
  ]);

  const filters = [
    {
      key: 'status',
      label: 'Status',
      filter: (
        <ChoiceList
          title="Status"
          titleHidden
          choices={[
            { label: 'Active', value: 'active' },
            { label: 'Inactive', value: 'inactive' },
          ]}
          selected={status}
          onChange={handleStatusChange}
        />
      ),
      shortcut: true,
    },
  ];

  const appliedFilters =
    status.length > 0
      ? [{ key: 'status', label: `Status: ${status.join(', ')}`, onRemove: () => setStatus([]) }]
      : [];

  if (error) {
    return (
      <Page title="Doctors">
        <Card>
          <EmptyState
            heading="Error loading doctors"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>Please try again later.</p>
          </EmptyState>
        </Card>
      </Page>
    );
  }

  return (
    <Page
      title="Doctors"
      primaryAction={{
        content: 'Add Doctor',
        onAction: () => setShowCreateModal(true),
      }}
    >
      <Card padding="0">
        <Box padding="400">
          <Filters
            queryValue={searchQuery}
            queryPlaceholder="Search doctors..."
            filters={filters}
            appliedFilters={appliedFilters}
            onQueryChange={handleSearchChange}
            onQueryClear={() => setSearchQuery('')}
            onClearAll={handleClearFilters}
          />
        </Box>

        {isLoading ? (
          <Box padding="400">
            <SkeletonBodyText lines={10} />
          </Box>
        ) : rows.length === 0 ? (
          <Box padding="800">
            <EmptyState
              heading="No doctors found"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              action={{ content: 'Add Doctor', onAction: () => setShowCreateModal(true) }}
            >
              <p>Add your first doctor to get started.</p>
            </EmptyState>
          </Box>
        ) : (
          <>
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'numeric', 'text', 'text']}
              headings={['Name', 'Email', 'Phone', 'Patient Count', 'Status', 'Action']}
              rows={rows}
            />
            {data && data.meta.totalPages > 1 && (
              <Box padding="400">
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={page > 1}
                    hasNext={page < data.meta.totalPages}
                    onPrevious={() => setPage((p) => p - 1)}
                    onNext={() => setPage((p) => p + 1)}
                  />
                </InlineStack>
              </Box>
            )}
          </>
        )}
      </Card>

      {data && (
        <Box paddingBlockStart="400">
          <Text as="p" tone="subdued" alignment="center">
            Showing {data.data.length} of {data.meta.total} doctors
          </Text>
        </Box>
      )}

      {/* Create Doctor Modal */}
      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Add New Doctor"
        primaryAction={{
          content: 'Add Doctor',
          onAction: handleCreateDoctor,
          disabled: !newDoctor.firstName || !newDoctor.lastName,
          loading: createMutation.isPending,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setShowCreateModal(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="First Name"
              value={newDoctor.firstName}
              onChange={(value) => setNewDoctor((prev) => ({ ...prev, firstName: value }))}
              autoComplete="off"
              requiredIndicator
            />
            <TextField
              label="Last Name"
              value={newDoctor.lastName}
              onChange={(value) => setNewDoctor((prev) => ({ ...prev, lastName: value }))}
              autoComplete="off"
              requiredIndicator
            />
            <TextField
              label="Email"
              type="email"
              value={newDoctor.email}
              onChange={(value) => setNewDoctor((prev) => ({ ...prev, email: value }))}
              autoComplete="off"
            />
            <TextField
              label="Phone"
              type="tel"
              value={newDoctor.phone}
              onChange={(value) => setNewDoctor((prev) => ({ ...prev, phone: value }))}
              autoComplete="off"
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
